/**
 * Option Snapshot Endpoint Builder
 * Encapsulates endpoint construction for Massive option snapshot API
 */

import { parseOccSymbol } from "../../../shared/symbols/occOptionSymbol";

export interface OptionSnapshotEndpoint {
  underlying: string;
  contractRaw: string; // Unprefixed OCC symbol
  contractMassive: string; // Prefixed "O:" + OCC symbol
  path: string; // Full endpoint path (uses contractMassive by default)
}

/**
 * Build endpoint for option snapshot request
 * @param contractSymbol - OCC option symbol (with or without "O:" prefix)
 * @returns Endpoint details including underlying, contract formats, and full path
 * @throws Error if contract symbol is invalid (e.g., dotted/hyphen tickers that don't match OCC format)
 */
export function buildOptionSnapshotEndpoint(contractSymbol: string): OptionSnapshotEndpoint {
  // Strip "O:" prefix if present (treat as Massive contract identifier)
  let symbolToParse = contractSymbol.trim();
  if (symbolToParse.startsWith("O:")) {
    symbolToParse = symbolToParse.substring(2).trim();
  }

  // Parse OCC symbol (throws if invalid)
  let parsed;
  try {
    parsed = parseOccSymbol(symbolToParse);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid OCC option symbol";
    throw new Error("Invalid OCC option symbol: " + message);
  }

  // Extract underlying from parsed OCC symbol
  const underlying = parsed.root;

  // Build contract formats
  const contractRaw = parsed.formats.unspaced;
  const contractMassive = "O:" + contractRaw;

  // Build path using contractMassive by default
  const path =
    "/v1/massive/v3/snapshot/options/" +
    encodeURIComponent(underlying) +
    "/" +
    encodeURIComponent(contractMassive);

  return {
    underlying,
    contractRaw,
    contractMassive,
    path,
  };
}
